<script setup>
import {
  NCard, NSpace, NH1, NH2, NH3, NP, NDivider,
  NButton, NIcon, NGrid, NGridItem, NStatistic, NTag, NGradientText,
  NTimeline, NTimelineItem, NAlert
} from 'naive-ui';
import { useI18n } from 'vue-i18n';
import {
  Search, UserCheck, Calendar, MessageCircle, Star,
  Shield, Clock, CheckCircle, Users, Scale,
  FileText, CreditCard, Phone, Video
} from 'lucide-vue-next';
import { dt } from '@/utils/i18n';

const { t, locale } = useI18n();

// Steps with icons
const mainSteps = [
  {
    icon: Search,
    title: 'howItWorks.steps.search.title',
    description: 'howItWorks.steps.search.description',
    color: '#36d1dc'
  },
  {
    icon: UserCheck,
    title: 'howItWorks.steps.verify.title', 
    description: 'howItWorks.steps.verify.description',
    color: '#5b86e5'
  },
  {
    icon: MessageCircle,
    title: 'howItWorks.steps.connect.title',
    description: 'howItWorks.steps.connect.description',
    color: '#ef473a'
  },
  {
    icon: Star,
    title: 'howItWorks.steps.review.title',
    description: 'howItWorks.steps.review.description',
    color: '#f7b733'
  }
];

// Features
const features = [
  {
    icon: Shield,
    title: 'howItWorks.features.verified.title',
    description: 'howItWorks.features.verified.description',
    color: '#11998e'
  },
  {
    icon: Clock,
    title: 'howItWorks.features.quick.title',
    description: 'howItWorks.features.quick.description', 
    color: '#36d1dc'
  },
  {
    icon: CheckCircle,
    title: 'howItWorks.features.quality.title',
    description: 'howItWorks.features.quality.description',
    color: '#5b86e5'
  }
];

// Stats
const stats = [
  { count: 2500, label: 'howItWorks.stats.lawyers', icon: Scale },
  { count: 15000, label: 'howItWorks.stats.consultations', icon: Users },
  { count: 98, label: 'howItWorks.stats.satisfaction', icon: Star, suffix: '%' }
];
</script>

<template>
  <div class="how-it-works-container">
    <!-- Hero Section -->
    <div class="hero-section">
      <NH1 class="hero-title">
        <NGradientText :gradient="{ from: '#36d1dc', to: '#5b86e5', deg: 45 }">
          {{ dt('howItWorks.heroTitle') }}
        </NGradientText>
      </NH1>
      <NP class="hero-subtitle">{{ dt('howItWorks.heroSubtitle') }}</NP>
    </div>

    <!-- Main Steps -->
    <div class="steps-section">
      <NH2 class="section-title">{{ dt('howItWorks.stepsTitle') }}</NH2>
      <div class="steps-grid">
        <div v-for="(step, index) in mainSteps" :key="index" class="step-card">
          <div class="step-number">{{ index + 1 }}</div>
          <div class="step-icon" :style="{ backgroundColor: step.color }">
            <component :is="step.icon" size="32" color="white" />
          </div>
          <NH3 class="step-title">{{ dt(step.title) }}</NH3>
          <NP class="step-description">{{ dt(step.description) }}</NP>
        </div>
      </div>
    </div>

    <!-- Detailed Process -->
    <NCard class="process-card">
      <NH2 class="section-heading">{{ dt('howItWorks.detailedProcess.title') }}</NH2>
      <NTimeline>
        <NTimelineItem 
          v-for="(item, index) in 6" 
          :key="index"
          :type="index === 5 ? 'success' : 'default'"
        >
          <template #header>
            <NH3>{{ dt(`howItWorks.detailedProcess.step${index + 1}.title`) }}</NH3>
          </template>
          <NP>{{ dt(`howItWorks.detailedProcess.step${index + 1}.description`) }}</NP>
          <div v-if="index === 1" class="verification-badges">
            <NTag type="success" size="small">
              <template #icon><Shield size="14" /></template>
              {{ dt('howItWorks.badges.verified') }}
            </NTag>
            <NTag type="info" size="small">
              <template #icon><FileText size="14" /></template>
              {{ dt('howItWorks.badges.licensed') }}
            </NTag>
            <NTag type="warning" size="small">
              <template #icon><Star size="14" /></template>
              {{ dt('howItWorks.badges.rated') }}
            </NTag>
          </div>
          <div v-if="index === 2" class="contact-options">
            <div class="contact-option">
              <Phone size="16" />
              <span>{{ dt('howItWorks.contactOptions.phone') }}</span>
            </div>
            <div class="contact-option">
              <Video size="16" />
              <span>{{ dt('howItWorks.contactOptions.video') }}</span>
            </div>
            <div class="contact-option">
              <MessageCircle size="16" />
              <span>{{ dt('howItWorks.contactOptions.chat') }}</span>
            </div>
          </div>
        </NTimelineItem>
      </NTimeline>
    </NCard>

    <!-- Features Section -->
    <div class="features-section">
      <NH2 class="section-title">{{ dt('howItWorks.featuresTitle') }}</NH2>
      <NGrid :cols="3" :x-gap="20" responsive="screen" :collapsed="['xs', 's']">
        <NGridItem v-for="(feature, index) in features" :key="index">
          <div class="feature-card">
            <div class="feature-icon" :style="{ backgroundColor: feature.color }">
              <component :is="feature.icon" size="28" color="white" />
            </div>
            <NH3 class="feature-title">{{ dt(feature.title) }}</NH3>
            <NP class="feature-description">{{ dt(feature.description) }}</NP>
          </div>
        </NGridItem>
      </NGrid>
    </div>

    <!-- Stats Section -->
    <div class="stats-section">
      <NGrid :cols="3" :x-gap="16" responsive="screen" :collapsed="['xs', 's']">
        <NGridItem v-for="(stat, index) in stats" :key="index">
          <NStatistic 
            :label="dt(stat.label)" 
            :value="stat.count"
            :value-style="{ color: '#36d1dc' }"
          >
            <template #prefix>
              <component :is="stat.icon" :size="24" color="#36d1dc" />
            </template>
            <template v-if="stat.suffix" #suffix>{{ stat.suffix }}</template>
          </NStatistic>
        </NGridItem>
      </NGrid>
    </div>

    <!-- Call to Action -->
    <NCard class="cta-card">
      <NH2 class="cta-title">{{ dt('howItWorks.cta.title') }}</NH2>
      <NP class="cta-text">{{ dt('howItWorks.cta.text') }}</NP>
      <NSpace justify="center" class="cta-buttons">
        <NButton type="primary" size="large" class="cta-button">
          {{ dt('howItWorks.cta.findLawyer') }}
        </NButton>
        <NButton size="large" class="cta-button-secondary">
          {{ dt('howItWorks.cta.joinAsLawyer') }}
        </NButton>
      </NSpace>
    </NCard>
  </div>
</template>

<style scoped>
.how-it-works-container {
  padding: 16px;
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  color: #333;
  overflow-x: hidden;
  box-sizing: border-box;
}

.hero-section {
  text-align: center;
  padding: 40px 0;
}

.hero-subtitle {
  font-size: 1.2rem;
  margin-top: 12px;
  color: #555;
}

.section-title {
  text-align: center;
  margin-bottom: 30px;
  font-size: 1.5rem;
  position: relative;
}

.section-title::after {
  content: "";
  display: block;
  width: 50px;
  height: 3px;
  background: linear-gradient(135deg, #36d1dc, #5b86e5);
  margin: 10px auto 0;
  border-radius: 2px;
}

.steps-section {
  margin: 60px 0;
}

.steps-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 30px;
  margin: 40px 0;
}

.step-card {
  background: white;
  border-radius: 16px;
  padding: 30px 20px;
  text-align: center;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
  position: relative;
}

.step-card:hover {
  transform: translateY(-5px);
}

.step-number {
  position: absolute;
  top: -15px;
  left: 50%;
  transform: translateX(-50%);
  width: 30px;
  height: 30px;
  background: linear-gradient(135deg, #36d1dc, #5b86e5);
  border-radius: 50%;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 14px;
}

.step-icon {
  width: 70px;
  height: 70px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 20px auto 16px;
}

.step-title {
  margin-bottom: 12px;
  color: #333;
}

.step-description {
  color: #666;
  line-height: 1.6;
}

.process-card {
  margin: 60px 0;
  border-radius: 16px;
  overflow: hidden;
}

.section-heading {
  text-align: center;
  margin-bottom: 30px;
}

.verification-badges {
  display: flex;
  gap: 8px;
  margin-top: 12px;
  flex-wrap: wrap;
}

.contact-options {
  display: flex;
  gap: 16px;
  margin-top: 12px;
  flex-wrap: wrap;
}

.contact-option {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #36d1dc;
  font-size: 14px;
}

.features-section {
  margin: 60px 0;
}

.feature-card {
  background: white;
  border-radius: 12px;
  padding: 24px;
  text-align: center;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s ease;
  height: 100%;
}

.feature-card:hover {
  transform: translateY(-3px);
}

.feature-icon {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
}

.feature-title {
  margin-bottom: 12px;
  color: #333;
}

.feature-description {
  color: #666;
  line-height: 1.6;
}

.stats-section {
  margin: 60px 0;
  padding: 40px;
  background: linear-gradient(135deg, rgba(54, 209, 220, 0.05), rgba(91, 134, 229, 0.05));
  border-radius: 16px;
}

.cta-card {
  text-align: center;
  padding: 40px;
  background: linear-gradient(135deg, rgba(54, 209, 220, 0.1), rgba(91, 134, 229, 0.1));
  border-radius: 16px;
  margin: 60px 0;
}

.cta-title {
  margin-bottom: 16px;
}

.cta-text {
  margin-bottom: 32px;
  color: #666;
  font-size: 1.1rem;
}

.cta-button {
  background: linear-gradient(135deg, #36d1dc, #5b86e5);
  border: none;
  padding: 0 32px;
  height: 48px;
  min-width: 160px;
}

.cta-button-secondary {
  border: 2px solid #36d1dc;
  color: #36d1dc;
  padding: 0 32px;
  height: 48px;
  min-width: 160px;
}

.cta-button-secondary:hover {
  background: #36d1dc;
  color: white;
}

@media screen and (max-width: 768px) {
  .steps-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }
  
  .contact-options {
    flex-direction: column;
    gap: 8px;
  }
  
  .verification-badges {
    justify-content: center;
  }
  
  .cta-buttons {
    flex-direction: column;
    gap: 12px;
  }
  
  .cta-button,
  .cta-button-secondary {
    width: 100%;
    max-width: 250px;
  }
}
</style>